import { type Editor, posToDOMRect } from '@tiptap/core';
import { TextSelection } from '@tiptap/pm/state';

/**
 * Floating-UI reference for the text toolbar. Anchors a non-empty text selection
 * to its start position instead of its full bounding box. Select-All spans the
 * whole note, whose rect is as tall as the document, leaving no room to flip the
 * toolbar above or below it — so it clips to an empty sliver. The start-line rect
 * keeps the toolbar fully visible, matching a single-line selection. Node / cell
 * / empty selections return null to keep TipTap's default anchoring.
 */
export const getSelectionStartAnchor = (
  editor: Editor
): { getBoundingClientRect: () => DOMRect } | null => {
  const { selection } = editor.state;
  if (!(selection instanceof TextSelection) || selection.empty) {
    return null;
  }
  const rect = posToDOMRect(editor.view, selection.from, selection.from);
  return { getBoundingClientRect: () => rect };
};

/**
 * Last non-empty line box from a selection's `Range.getClientRects()`.
 * Multi-line / wrapping selections have a full bounding box as wide as the
 * text column; using only the last line keeps floating UI next to the end of
 * the highlight instead of the column edge.
 */
export const pickLastNonEmptyClientRect = (
  rects: ArrayLike<DOMRectReadOnly>
): DOMRectReadOnly | null => {
  for (let i = rects.length - 1; i >= 0; i--) {
    const rect = rects[i];
    if (rect && (rect.width > 0 || rect.height > 0)) {
      return rect;
    }
  }
  return null;
};
